import React, { useMemo, useState } from 'react';
import { COSTOS_FIJOS, fmtM } from '../utils';
import { Btn } from '../components/UI';
import { showToast } from '../components/UI';

export default function HistorialCostos({ store }) {
  const { movimientos } = store;
  const [exportando, setExportando] = useState(false);

  // Build month list from movements
  const meses = useMemo(() => {
    const set = new Set();
    Object.values(movimientos || {}).forEach(m => {
      if (m.fecha) set.add(m.fecha.slice(0, 7));
    });
    return Array.from(set).sort();
  }, [movimientos]);

  // For each month, aggregate costs from payments
  const tabla = useMemo(() => {
    return meses.map(mes => {
      const pagos = Object.values(movimientos || {}).filter(m =>
        (m.tipo === 'pago_proveedor' || m.tipo === 'gasto') && m.fecha?.slice(0, 7) === mes
      );

      // Costos financieros (res financieros) - placeholder
      const costoFinanciero = 0;

      const row = { mes, costoFinanciero };
      COSTOS_FIJOS.forEach(nombre => {
        const total = pagos
          .filter(m => (m.desc || '').toLowerCase().includes(nombre.toLowerCase()) || (m.concepto || '').toLowerCase().includes(nombre.toLowerCase()))
          .reduce((a, m) => a + (parseFloat(m.monto) || 0), 0);
        row[nombre] = total;
      });

      // Variables acumulados (gastos tipo gasto sin categoria de costos fijos)
      row['Variables'] = pagos.filter(m => m.tipo === 'gasto').reduce((a, m) => a + (parseFloat(m.monto) || 0), 0);

      row['Total'] = COSTOS_FIJOS.reduce((a, n) => a + (row[n] || 0), 0) + row['Variables'];
      return row;
    });
  }, [meses, movimientos]);

  const columnas = ['Mes', 'Costos financieros', ...COSTOS_FIJOS, 'Variables', 'Total'];

  function exportCSV() {
    setExportando(true);
    const headers = columnas.join(',');
    const rows = tabla.map(row => {
      return [
        row.mes,
        row.costoFinanciero || 0,
        ...COSTOS_FIJOS.map(n => row[n] || 0),
        row['Variables'] || 0,
        row['Total'] || 0,
      ].map(v => typeof v === 'number' ? v.toFixed(2) : v).join(',');
    });
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `costos_farmacia_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setExportando(false);
    showToast('CSV descargado — abrilo en Google Sheets');
  }

  function exportSheetsUrl() {
    // Build CSV and encode for Google Sheets import
    showToast('Descargá el CSV y abrilo en Drive → Nueva hoja de cálculo → Archivo → Importar');
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Historial de costos por mes</div>
          <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 2 }}>{tabla.length} meses registrados</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="grn" onClick={exportCSV} disabled={exportando}>
            {exportando ? 'Exportando...' : '⬇ Exportar CSV para Sheets'}
          </Btn>
        </div>
      </div>

      {tabla.length === 0 ? (
        <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', padding: 40, textAlign: 'center', color: 'var(--tx3)' }}>
          Sin datos de costos registrados. Cargá pagos en la sección de Costos fijos y Gastos variables.
        </div>
      ) : (
        <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', overflow: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 11, whiteSpace: 'nowrap' }}>
            <thead>
              <tr style={{ background: 'var(--surf2)', position: 'sticky', top: 0 }}>
                <th style={{ textAlign: 'left', padding: '9px 14px', fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', textTransform: 'uppercase', position: 'sticky', left: 0, background: 'var(--surf2)' }}>Mes</th>
                <th style={{ textAlign: 'right', padding: '9px 14px', fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', textTransform: 'uppercase' }}>Costos financ.</th>
                {COSTOS_FIJOS.map(n => (
                  <th key={n} style={{ textAlign: 'right', padding: '9px 14px', fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', textTransform: 'uppercase' }}>{n}</th>
                ))}
                <th style={{ textAlign: 'right', padding: '9px 14px', fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', textTransform: 'uppercase' }}>Variables</th>
                <th style={{ textAlign: 'right', padding: '9px 14px', fontSize: 9.5, fontWeight: 700, color: 'var(--red)', borderBottom: '1px solid var(--border)', textTransform: 'uppercase' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {tabla.map((row, i) => (
                <tr key={row.mes} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? '' : 'var(--surf2)' }}>
                  <td style={{ padding: '8px 14px', fontWeight: 700, fontFamily: 'Courier, monospace', borderRight: '1px solid var(--border)', position: 'sticky', left: 0, background: i % 2 === 0 ? 'var(--surf)' : 'var(--surf2)' }}>{row.mes}</td>
                  <td style={{ padding: '8px 14px', textAlign: 'right', fontFamily: 'Courier, monospace', borderRight: '1px solid var(--border)', color: row.costoFinanciero > 0 ? 'var(--red)' : 'var(--tx3)' }}>
                    {row.costoFinanciero > 0 ? fmtM(row.costoFinanciero) : '—'}
                  </td>
                  {COSTOS_FIJOS.map(n => (
                    <td key={n} style={{ padding: '8px 14px', textAlign: 'right', fontFamily: 'Courier, monospace', borderRight: '1px solid var(--border)', color: (row[n] || 0) > 0 ? 'var(--tx)' : 'var(--tx3)' }}>
                      {(row[n] || 0) > 0 ? fmtM(row[n]) : '—'}
                    </td>
                  ))}
                  <td style={{ padding: '8px 14px', textAlign: 'right', fontFamily: 'Courier, monospace', borderRight: '1px solid var(--border)', color: (row['Variables'] || 0) > 0 ? 'var(--tx)' : 'var(--tx3)' }}>
                    {(row['Variables'] || 0) > 0 ? fmtM(row['Variables']) : '—'}
                  </td>
                  <td style={{ padding: '8px 14px', textAlign: 'right', fontFamily: 'Courier, monospace', fontWeight: 700, color: 'var(--red)' }}>
                    {fmtM(row['Total'])}
                  </td>
                </tr>
              ))}
              {/* Totals row */}
              <tr style={{ background: 'var(--surf2)', borderTop: '2px solid var(--border2)' }}>
                <td style={{ padding: '9px 14px', fontWeight: 700, fontSize: 11, borderRight: '1px solid var(--border)', position: 'sticky', left: 0, background: 'var(--surf2)' }}>TOTAL</td>
                <td style={{ padding: '9px 14px', textAlign: 'right', fontFamily: 'Courier, monospace', fontWeight: 700, borderRight: '1px solid var(--border)' }}>
                  {fmtM(tabla.reduce((a, r) => a + (r.costoFinanciero || 0), 0))}
                </td>
                {COSTOS_FIJOS.map(n => (
                  <td key={n} style={{ padding: '9px 14px', textAlign: 'right', fontFamily: 'Courier, monospace', fontWeight: 700, borderRight: '1px solid var(--border)' }}>
                    {fmtM(tabla.reduce((a, r) => a + (r[n] || 0), 0))}
                  </td>
                ))}
                <td style={{ padding: '9px 14px', textAlign: 'right', fontFamily: 'Courier, monospace', fontWeight: 700, borderRight: '1px solid var(--border)' }}>
                  {fmtM(tabla.reduce((a, r) => a + (r['Variables'] || 0), 0))}
                </td>
                <td style={{ padding: '9px 14px', textAlign: 'right', fontFamily: 'Courier, monospace', fontWeight: 700, color: 'var(--red)' }}>
                  {fmtM(tabla.reduce((a, r) => a + (r['Total'] || 0), 0))}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--blu-l)', borderRadius: 8, fontSize: 11, color: 'var(--blu-m)' }}>
        <strong>Para abrir en Google Sheets:</strong> Descargá el CSV → abrí Google Drive → arrastrá el archivo → abrilo con Google Sheets. O desde Sheets: Archivo → Importar → Subir archivo.
      </div>
    </div>
  );
}
